import React, { useMemo, useState } from 'react';
import { DatePicker, Form, Input, Message, Tag } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import PageHeader from '../components/shared/PageHeader';
import { useDeleteMileageRate, useMileageRatesAdmin, useSaveMileageRate } from '../hooks/useQueries';
import { formatIsoDate, formatMileageRate } from '../utils/mileage';

const IRS_RATES_URL = 'https://www.irs.gov/tax-professionals/standard-mileage-rates';

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const fromIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

const formatTimestamp = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US') : '';
};

const EMPTY_DRAFT = { id: null, effectiveDate: null, rate: '', notes: '' };

// The dated history of the mileage reimbursement rate. Expenses are priced at
// the rate in force on the trip date, so a new rate is added with the date it
// takes effect rather than by overwriting the current one; the rows are the
// log of what was in force when.
export default function MileageRatesPage() {
  const ratesQuery = useMileageRatesAdmin({ enabled: true });
  const saveMutation = useSaveMileageRate();
  const deleteMutation = useDeleteMileageRate();

  const [draft, setDraft] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const draftShown = useLastPresent(draft); // keeps the dialog steady while it fades out

  const rows = useMemo(() => ratesQuery.data || [], [ratesQuery.data]);
  const todayIso = toIsoDate(new Date());
  // Newest first, so the first row on or before today is the one in force.
  const currentId = rows.find((row) => row.effective_date <= todayIso)?.id ?? null;

  const openNew = () => {
    setDraft(EMPTY_DRAFT);
    setConfirmDelete(false);
  };

  const openEdit = (row) => {
    setDraft({
      id: row.id,
      effectiveDate: fromIsoDate(row.effective_date),
      rate: String(row.rate_per_mile),
      notes: row.notes || '',
    });
    setConfirmDelete(false);
  };

  const closeDraft = () => setDraft(null);

  const rateNumber = Number(draft?.rate);
  const draftValid = Boolean(draft?.effectiveDate) && Number.isFinite(rateNumber) && rateNumber > 0 && rateNumber < 10;

  const submitDraft = async () => {
    if (!draftValid) return;
    try {
      await saveMutation.mutateAsync({
        id: draft.id,
        effective_date: toIsoDate(draft.effectiveDate),
        rate_per_mile: rateNumber,
        notes: draft.notes.trim(),
      });
      showToast('success', draft.id ? 'Mileage rate updated.' : 'Mileage rate added.');
      setDraft(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save the rate'));
    }
  };

  const submitDelete = async () => {
    if (!draft?.id) return;
    try {
      await deleteMutation.mutateAsync(draft.id);
      showToast('success', 'Mileage rate deleted.');
      setDraft(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to delete the rate'));
    }
  };

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Configuration', to: '/admin/configuration' },
          { label: 'Mileage Rates' },
        ]}
        icon="bi-speedometer2"
        title="Mileage Rates"
      />

      <p className="text-muted">
        Mileage expenses are priced at the rate in effect on the trip date. When the IRS announces a
        new <a href={IRS_RATES_URL} target="_blank" rel="noreferrer">standard business mileage rate</a>,
        add it with the date it takes effect. Expenses already filed keep the rate they were priced at.
      </p>

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">Rate History</h3>
            <span className="bdc-count">{rows.length}</span>
          </div>
          <div className="bdc-table-head__actions">
            <button type="button" className="bdc-btn bdc-btn--sm bdc-btn--primary" onClick={openNew}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Add rate
            </button>
          </div>
        </div>

        {ratesQuery.error ? (
          <Message type="error" showIcon>
            {String(ratesQuery.error?.message || ratesQuery.error)}
          </Message>
        ) : null}

        <table className="nsi-table bdc-table--narrow">
          <thead>
            <tr>
              <th style={{ width: 170 }}>Effective</th>
              <th style={{ width: 130 }} className="nsi-table__right">Rate per mile</th>
              <th>Notes</th>
              <th style={{ width: 200 }}>Entered by</th>
            </tr>
          </thead>
          <tbody>
            {ratesQuery.isLoading ? (
              <tr>
                <td colSpan={4} className="nsi-table__empty">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="nsi-table__empty">
                  No rates yet. Mileage expenses can't be priced until one is added.
                </td>
              </tr>
            ) : rows.map((row) => (
              <tr
                key={row.id}
                className="nsi-table__row"
                onClick={() => openEdit(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEdit(row);
                  }
                }}
                tabIndex={0}
                role="button"
                title="Edit this rate"
              >
                <td>
                  {formatIsoDate(row.effective_date)}
                  {row.id === currentId ? <Tag color="blue" size="sm" className="ms-2">Current</Tag> : null}
                  {row.effective_date > todayIso ? <Tag color="orange" size="sm" className="ms-2">Upcoming</Tag> : null}
                </td>
                <td className="nsi-table__right bdc-num fw-semibold">{formatMileageRate(row.rate_per_mile)}</td>
                <td className="bdc-table-wrap">{row.notes || <span className="nsi-table__muted">—</span>}</td>
                <td className="bdc-table-wrap">
                  {row.created_by_name || <span className="nsi-table__muted">Seeded</span>}
                  {row.created_at ? <span className="nsi-table__muted"> · {formatTimestamp(row.created_at)}</span> : null}
                  {row.updated_at ? (
                    <div className="nsi-table__muted small">
                      Edited{row.updated_by_name ? ` by ${row.updated_by_name}` : ''} · {formatTimestamp(row.updated_at)}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!draft} onClose={closeDraft} size="xs">
        <Modal.Header>
          <Modal.Title>{draftShown?.id ? 'Edit Mileage Rate' : 'Add Mileage Rate'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {draftShown ? (
            <Form fluid>
              <Form.Group controlId="mileage_rate_effective_date">
                <Form.ControlLabel>Effective date</Form.ControlLabel>
                <DatePicker
                  value={draftShown.effectiveDate}
                  onChange={(value) => setDraft((prev) => ({ ...prev, effectiveDate: value }))}
                  format="MM/dd/yyyy"
                  oneTap
                  block
                  placement="bottomStart"
                />
                <Form.HelpText>The first trip date this rate applies to.</Form.HelpText>
              </Form.Group>
              <Form.Group controlId="mileage_rate_value">
                <Form.ControlLabel>Rate per mile ($)</Form.ControlLabel>
                <Input
                  value={draftShown.rate}
                  onChange={(value) => setDraft((prev) => ({ ...prev, rate: String(value).replace(/[^0-9.]/g, '') }))}
                  placeholder="0.725"
                  inputMode="decimal"
                />
                <Form.HelpText>In dollars: 72.5 cents is 0.725.</Form.HelpText>
              </Form.Group>
              <Form.Group controlId="mileage_rate_notes">
                <Form.ControlLabel>Notes</Form.ControlLabel>
                <Input
                  as="textarea"
                  rows={2}
                  value={draftShown.notes}
                  onChange={(value) => setDraft((prev) => ({ ...prev, notes: value }))}
                  placeholder="e.g. IRS Notice 2026-10"
                />
              </Form.Group>
              {draftShown.id ? (
                <Message type="info" showIcon>
                  Changing a rate doesn't reprice expenses already filed. An open expense picks up the
                  change the next time it's edited.
                </Message>
              ) : null}
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer
          left={draftShown?.id ? (
            confirmDelete ? (
              <Button variant="danger-solid" icon="trash" loading={deleteMutation.isPending} onClick={submitDelete}>
                Confirm delete
              </Button>
            ) : (
              <Button variant="danger" icon="trash" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )
          ) : null}
        >
          <Button onClick={closeDraft}>Cancel</Button>
          <Button
            variant="primary"
            loading={saveMutation.isPending}
            disabled={!draftValid}
            onClick={submitDraft}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
